import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND_LOGOS } from "@/lib/site";

const NAV_LINKS = [
  { label: "Services", id: "services" },
  { label: "Brands", id: "brands" },
  { label: "Why Us", id: "why" },
  { label: "Leadership", id: "leadership" },
  { label: "Coverage", id: "coverage" },
  { label: "Reviews", id: "reviews" },
  { label: "Contact", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0B1120]/90 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
          data-testid="header-logo-button"
        >
          <img
            src={BRAND_LOGOS.mastersText}
            alt="Master Ready Services"
            className="h-10 w-auto object-contain"
          />
          <div className="hidden md:flex flex-col leading-none">
            <span className="font-display text-[11px] tracking-[0.25em] text-slate-400">
              MASTER READY
            </span>
            <span className="font-display text-[11px] tracking-[0.25em] text-orange-500">
              SERVICES, LLC
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="px-4 py-2 font-tech text-[12px] text-slate-300 hover:text-orange-400 transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/portal"
            data-testid="nav-link-portal"
            className="px-4 py-2 font-tech text-[12px] text-slate-300 hover:text-amber-300 transition-colors border-l border-white/10 ml-2"
          >
            Client Portal
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollTo("request")}
            data-testid="header-cta-request"
            className="hidden md:inline-flex btn-forge text-[12px] py-2.5 px-4"
          >
            Request Campaign
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-slate-100"
            data-testid="header-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0B1120]/98 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`mobile-nav-${l.id}`}
                className="text-left px-4 py-3 font-display text-lg text-slate-200 hover:text-orange-400 border-b border-white/5"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/portal"
              data-testid="mobile-nav-portal"
              onClick={() => setOpen(false)}
              className="px-4 py-3 font-display text-lg text-amber-300 border-b border-white/5"
            >
              Client Portal
            </Link>
            <button
              onClick={() => scrollTo("request")}
              data-testid="mobile-cta-request"
              className="btn-forge mt-4"
            >
              Request Campaign
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
